// Meter detail panel — the chart/stats/readings block for one meter. Embedded
// inline on the home dashboard (the legacy portal's pattern: pick a card, the
// chart appears below — no page jump) and reused by the /meter/:mid deep-link
// route. Day/Week/Month/Year views with prev/next period stepping plus a free
// Custom date range, hero stats, consumption/reading chart, the device's
// alerts (with owner acknowledge), device & product info, consumption/readings
// table and CSV export.

import type { ReactElement } from 'react';
import { lazy, Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { onAlertAcknowledged, withAcknowledged } from '../lib/alertEvents';
import {
  getMyMeter,
  getMyMeterProduct,
  openAlerts,
  type MeterDetail,
  type MeterProductInfo,
} from '../lib/api';
import { meterBrand } from '../lib/colors';
import {
  GRANULARITY_BY_VIEW,
  customGranularity,
  customRangeEnd,
  customRangeTitle,
  endOfPeriod,
  formatDateTime,
  formatValue,
  periodTitle,
  seedCustomRange,
  startOfPeriod,
  stepPeriod,
  tooltipDateLabel,
  VIEW_MODE_KEYS,
  viewModeLabel,
  type CustomRange,
  type ViewMode,
} from '../lib/meterCharts';
import { useTheme } from '../lib/theme';
import { fromWall, LEGACY_ZONE, wallNow, wallOf } from '../lib/zone';
import Icon from './Icon';
import MdiIcon from './MdiIcon';
import MeterAlertsModal from './MeterAlertsModal';
import type { ChartKind } from './MeterChart';
import MeterNotifyModal from './MeterNotifyModal';
import PeriodPicker from './PeriodPicker';
import RangePicker from './RangePicker';
import { Modal, Segmented, Spinner, StateCard } from './ui';

// The chart pulls in the whole charting library — lazy so the dashboard's
// initial bundle stays lean.
const MeterChart = lazy(() => import('./MeterChart'));

function downloadCsv(filename: string, rows: (string | number)[][]): void {
  const csv = rows.map((r) => r.join(',')).join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function MeterPanel({
  mid,
  pid,
  group,
  gname,
  pname,
  period,
  tz = LEGACY_ZONE,
}: {
  mid: string;
  pid: string;
  /** Group/category context — forwarded so the backend clamps readings to the
   *  membership's occupancy window. */
  group?: { type: 'g' | 'c'; id: string };
  gname?: string | null;
  pname?: string | null;
  /** Controlled period (the dashboard's global Day/Week/Month/Year/Custom
   *  selection). When set, the panel follows it and hides its own period
   *  toolbar; when absent (deep-link page) the panel manages its own. */
  period?: { view: ViewMode; anchor: Date } | { view: 'custom'; range: CustomRange };
  /** The group's project timezone (lib/zone.ts): periods are its days. */
  tz?: string;
}): ReactElement {
  const { t } = useTranslation();
  const { mode } = useTheme();

  const controlled = !!period;
  const [ownView, setOwnView] = useState<ViewMode | 'custom'>('month');
  const [ownAnchor, setOwnAnchor] = useState<Date>(() => wallNow(tz));
  const [ownRange, setOwnRange] = useState<CustomRange>(() =>
    seedCustomRange(wallNow(tz), 'month', null, null, wallNow(tz)),
  );
  const view = period?.view ?? ownView;
  const anchor = period && period.view !== 'custom' ? period.anchor : ownAnchor;
  const range = period?.view === 'custom' ? period.range : ownRange;
  const [meter, setMeter] = useState<MeterDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [product, setProduct] = useState<MeterProductInfo | null>(null);
  const [kind, setKind] = useState<ChartKind | null>(null); // null until meter loads
  const [deviceOpen, setDeviceOpen] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [alertsOpen, setAlertsOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    setError(null);
    setRefreshing(true);
    const from = view === 'custom' ? range.from : startOfPeriod(anchor, view);
    const to = view === 'custom' ? customRangeEnd(range) : endOfPeriod(from, view);
    const granularity = view === 'custom' ? customGranularity(range) : GRANULARITY_BY_VIEW[view];
    getMyMeter(
      mid,
      pid,
      {
        from: fromWall(from, tz).toISOString(),
        to: fromWall(to, tz).toISOString(),
        granularity,
        tz,
      },
      group,
    )
      .then((m) => {
        if (!alive) return;
        setMeter(m);
        setKind((k) => k ?? (m.type === 'sum' ? 'consumption' : 'reading'));
      })
      .catch((e: unknown) => {
        if (alive) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (alive) setRefreshing(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- group identity is (type,id)
  }, [mid, pid, group?.type, group?.id, view, anchor, range, tz]);

  // An alert acknowledged in the alerts modal: keep the alerts button's count
  // honest without refetching the readings.
  useEffect(
    () =>
      onAlertAcknowledged(({ deveui, alert }) =>
        setMeter((m) => {
          if (!m || (m.deveui ?? '').toLowerCase() !== deveui.toLowerCase()) return m;
          const alerts = withAcknowledged(m.alerts, alert);
          return alerts === m.alerts ? m : { ...m, alerts };
        }),
      ),
    [],
  );

  // Product info is fetched lazily the first time the device modal opens —
  // same as the mobile device sheet, so the panel load never waits on the
  // planner/product-DB round-trip.
  useEffect(() => {
    if (!deviceOpen || !meter || product) return;
    let alive = true;
    getMyMeterProduct(meter.mid, pid, meter.deveui)
      .then((p) => {
        if (alive) setProduct(p ?? {});
      })
      .catch(() => {
        if (alive) setProduct({});
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceOpen, meter?.mid]);

  // Bound the period navigator to the user's occupancy window. Custom has no
  // stepper — the range picker replaces it.
  const minDate = meter?.min_date ? wallOf(meter.min_date, tz) : null;
  const maxDate = meter?.max_date ? wallOf(meter.max_date, tz) : null;
  const periodStart = view === 'custom' ? range.from : startOfPeriod(anchor, view);
  const canPrev =
    view !== 'custom' &&
    (!minDate || endOfPeriod(stepPeriod(periodStart, view, -1), view) > minDate);
  const nextStart = view === 'custom' ? null : stepPeriod(periodStart, view, 1);
  const canNext =
    nextStart !== null && nextStart <= wallNow(tz) && (!maxDate || nextStart <= maxDate);

  const isSum = meter?.type === 'sum';
  const chartKind: ChartKind = kind ?? (isSum ? 'consumption' : 'reading');

  if (error) {
    return <StateCard title={t('meter.errorLoad')} body={error} />;
  }

  if (!meter) return <Spinner />;

  const brand = meterBrand(meter.color);
  const rangeTitle = view === 'custom' ? customRangeTitle(range) : periodTitle(anchor, view);
  const iconColor = mode === 'dark' ? brand.onDark : brand.solid;
  const bubbleBg = mode === 'dark' ? `rgba(${brand.rgbString}, 0.22)` : brand.soft;
  const productHasData = !!(product?.manufacturer || product?.model || product?.name);
  // The table and the CSV export list the chart's buckets (empty slots
  // skipped) — the legacy portal's "Loaded data" table and the mobile export:
  // each bucket's reading, plus its consumption for sum meters. The table runs
  // newest first and emphasises the column the chart plots.
  const filled = (meter.period.buckets ?? []).filter(
    (b) => (b.reading ?? b.average ?? b.consumption ?? null) !== null,
  );
  const rows = [...filled].reverse();
  const colClass = (k: ChartKind) => `num${isSum && chartKind === k ? ' active' : ''}`;

  // Only alarms nobody has handled turn the alerts button red — what
  // app.divako.io and the mobile app count as active.
  const open = openAlerts(meter.alerts).length;
  const alertsTip =
    open > 0 ? t('alerts.tileActiveA11y', { count: open }) : t('alerts.pageTitle');

  const exportCsv = () => {
    // Dates as the project's local calendar slot, like the chart.
    const len = { hour: 16, day: 10, month: 7 }[meter.period.granularity];
    const csv: (string | number)[][] = [
      [
        t('meter.export.colDate'),
        ...(isSum ? [`${t('meter.consumption')} (${meter.unit})`] : []),
        `${t('meter.reading')} (${meter.unit})`,
      ],
      ...filled.map((b) => [
        wallOf(b.date, tz).toISOString().slice(0, len).replace('T', ' '),
        ...(isSum ? [b.consumption ?? ''] : []),
        b.reading ?? b.average ?? '',
      ]),
    ];
    const from = meter.period.from.slice(0, 10);
    const to = meter.period.to.slice(0, 10);
    downloadCsv(`${meter.serial || meter.mid}_${from}_${to}.csv`, csv);
  };

  return (
    <>
      <div className="page-head">
        <span className="icon-bubble" style={{ background: bubbleBg, color: iconColor }}>
          <MdiIcon name={meter.icon} size={20} />
        </span>
        <div className="titles">
          <h1>{meter.name}</h1>
          <div className="subtitle">
            {[
              meter.serial ? t('home.serial', { serial: meter.serial }) : null,
              [gname, pname].filter(Boolean).join(' · ') || null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </div>
        </div>
        {meter.deveui && (
          <button
            className={`icon-btn${open > 0 ? ' danger' : ''}`}
            onClick={() => setAlertsOpen(true)}
            data-tip={alertsTip}
            aria-label={alertsTip}
          >
            <Icon name="alert-triangle" size={20} />
            {open > 0 && <span className="nav-badge">{open > 99 ? '99+' : open}</span>}
          </button>
        )}
        <button
          className="icon-btn"
          onClick={() => setNotifyOpen(true)}
          data-tip={t('meter.notify.button')}
          aria-label={t('meter.notify.button')}
        >
          <MdiIcon name="mdi-bell-cog-outline" size={22} />
        </button>
        <button
          className="icon-btn"
          onClick={() => setDeviceOpen(true)}
          data-tip={t('meter.device.title')}
          aria-label={t('meter.device.title')}
        >
          <Icon name="cpu" size={20} />
        </button>
      </div>

      {!controlled && (
        <div className="home-toolbar">
          <Segmented
            options={[
              ...VIEW_MODE_KEYS.map((v) => ({
                value: v as ViewMode | 'custom',
                label: viewModeLabel(v),
              })),
              { value: 'custom', label: t('common.custom') },
            ]}
            value={view}
            onChange={(v) => {
              if (v === 'custom') {
                // Seed the range from the period currently on screen so the
                // switch keeps showing the same window.
                if (view !== 'custom')
                  setOwnRange(seedCustomRange(anchor, view, minDate, maxDate, wallNow(tz)));
                setOwnView('custom');
                return;
              }
              setOwnView(v);
              setOwnAnchor(wallNow(tz));
            }}
          />
          <div className="spacer" />
          <div className="period-nav">
            {view === 'custom' ? (
              <RangePicker
                range={range}
                min={minDate}
                max={maxDate}
                now={wallNow(tz)}
                onChange={setOwnRange}
              />
            ) : (
              <>
                <button
                  className="icon-btn"
                  disabled={!canPrev}
                  onClick={() => setOwnAnchor(stepPeriod(periodStart, view, -1))}
                  data-tip={t('ui.previousPeriod')}
                  aria-label={t('ui.previousPeriod')}
                >
                  <Icon name="chevron-left" size={20} />
                </button>
                <PeriodPicker
                  view={view}
                  anchor={anchor}
                  min={minDate}
                  max={maxDate}
                  now={wallNow(tz)}
                  onChange={setOwnAnchor}
                />
                <button
                  className="icon-btn"
                  disabled={!canNext}
                  onClick={() => nextStart && setOwnAnchor(nextStart)}
                  data-tip={t('ui.nextPeriod')}
                  aria-label={t('ui.nextPeriod')}
                >
                  <Icon name="chevron-right" size={20} />
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <div className="card chart-card" style={{ opacity: refreshing ? 0.6 : 1 }}>
        <div className="chart-tools">
          <Segmented
            options={[
              ...(isSum
                ? [{ value: 'consumption' as ChartKind, label: t('meter.consumption') }]
                : []),
              { value: 'reading' as ChartKind, label: t('meter.reading') },
            ]}
            value={chartKind}
            onChange={setKind}
          />
          <div className="spacer" />
          <button className="btn" onClick={exportCsv} disabled={rows.length === 0}>
            <Icon name="download" size={16} />
            {t('meter.export.button')}
          </button>
        </div>
        <Suspense fallback={<Spinner />}>
          <MeterChart
            period={meter.period}
            comparison={meter.comparison}
            kind={chartKind}
            unit={meter.unit}
            colorName={meter.color}
            lastDate={meter.last_date}
            tz={tz}
          />
        </Suspense>
      </div>

      {alertsOpen && meter.deveui && (
        <MeterAlertsModal
          mid={meter.mid}
          pid={pid}
          group={group}
          deveui={meter.deveui}
          name={meter.device_name || meter.name}
          onClose={() => setAlertsOpen(false)}
        />
      )}

      {notifyOpen && (
        <MeterNotifyModal
          mid={meter.mid}
          pid={pid}
          meterName={meter.name}
          unit={meter.unit}
          isSum={isSum}
          currentValue={
            meter.last_value !== null && meter.last_value !== undefined
              ? formatValue(meter.last_value)
              : null
          }
          onClose={() => setNotifyOpen(false)}
        />
      )}

      {deviceOpen && (
        <Modal title={t('meter.device.title')} onClose={() => setDeviceOpen(false)}>
          {product?.image && (
            <img
              className="product-img"
              src={product.image}
              alt={product.model ?? product.name ?? ''}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          )}
          <div className="profile-row">
            <span className="row-label">{t('meter.device.serial')}</span>
            <span className="row-value">{meter.serial || '—'}</span>
          </div>
          {meter.device_name && (
            <div className="profile-row">
              <span className="row-label">{t('meter.device.deviceName')}</span>
              <span className="row-value">{meter.device_name}</span>
            </div>
          )}
          {product === null && <p className="muted">{t('meter.device.lookingUp')}</p>}
          {productHasData && (
            <>
              {product?.manufacturer && (
                <div className="profile-row">
                  <span className="row-label">{t('meter.device.manufacturer')}</span>
                  <span className="row-value">{product.manufacturer}</span>
                </div>
              )}
              {product?.model && (
                <div className="profile-row">
                  <span className="row-label">{t('meter.device.model')}</span>
                  <span className="row-value">{product.model}</span>
                </div>
              )}
              {product?.device_type && (
                <div className="profile-row">
                  <span className="row-label">{t('meter.device.deviceType')}</span>
                  <span className="row-value">{product.device_type}</span>
                </div>
              )}
            </>
          )}
          {meter.last_date && (
            <div className="profile-row">
              <span className="row-label">{t('meter.device.lastDataDate')}</span>
              <span className="row-value">{formatDateTime(meter.last_date)}</span>
            </div>
          )}
        </Modal>
      )}

      {rows.length > 0 && (
        <div className="card">
          <div className="card-title">
            <Icon name="chart" size={16} />
            {rangeTitle}
          </div>
          <div className="table-wrap">
            <table className="readings">
              <thead>
                <tr>
                  <th scope="col">{t('meter.export.colDate')}</th>
                  {isSum && (
                    <th scope="col" className={colClass('consumption')}>
                      {t('meter.consumption')} ({meter.unit})
                    </th>
                  )}
                  <th scope="col" className={colClass('reading')}>
                    {t('meter.reading')} ({meter.unit})
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 200).map((b) => (
                  <tr key={b.date}>
                    <td>{tooltipDateLabel(b, meter.period.granularity, tz)}</td>
                    {isSum && <td className={colClass('consumption')}>{formatValue(b.consumption)}</td>}
                    <td className={colClass('reading')}>{formatValue(b.reading ?? b.average)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rows.length > 200 && (
            <p className="chart-note">
              200 / {rows.length} ·{' '}
              {t('meter.export.rangeNote', { range: rangeTitle })}
            </p>
          )}
        </div>
      )}
    </>
  );
}
